<template>
  <button
    v-show="visible"
    type="button"
    class="mobile-ai-floating-chip"
    :style="chipStyle"
    :aria-label="ariaLabel"
    @click="$emit('click')"
  >
    AI
  </button>
</template>

<script>
/**
 * 우측 하단 새로고침 FAB(RefreshButton)과 겹치지 않도록,
 * 화면에 보이는 새로고침 버튼 위로 bottom 오프셋을 동적으로 맞춥니다.
 */
const MIN_ABOVE_BOTTOM_NAV_PX = 56 + 10
const GAP_ABOVE_REFRESH_PX = 8

export default {
  name: 'MobileAiFloatingChip',
  props: {
    visible: { type: Boolean, default: true },
    /** 라우트 등 변경 시 재측정 트리거 */
    layoutTick: { type: [String, Number], default: undefined },
    ariaLabel: { type: String, default: 'AI 도우미 열기' },
  },
  data() {
    return {
      dynamicBottomPx: MIN_ABOVE_BOTTOM_NAV_PX,
    }
  },
  computed: {
    chipStyle() {
      return {
        bottom: `calc(${this.dynamicBottomPx}px + env(safe-area-inset-bottom, 0px))`,
      }
    },
  },
  watch: {
    visible(v) {
      if (v) this.$nextTick(() => this.measure())
    },
    layoutTick() {
      this.$nextTick(() => this.measure())
    },
  },
  mounted() {
    this.scheduleDebounced = this.debounce(this.measure, 80)
    window.addEventListener('resize', this.scheduleDebounced)
    window.addEventListener('scroll', this.scheduleDebounced, { passive: true })
    this.observer = new MutationObserver(this.scheduleDebounced)
    this.observer.observe(document.body, {
      subtree: true,
      childList: true,
      attributes: true,
      attributeFilter: ['style', 'class'],
    })
    this.$nextTick(() => this.measure())
  },
  beforeDestroy() {
    if (this.scheduleDebounced) {
      window.removeEventListener('resize', this.scheduleDebounced)
      window.removeEventListener('scroll', this.scheduleDebounced)
    }
    if (this.observer) this.observer.disconnect()
    if (this._debTimer) clearTimeout(this._debTimer)
  },
  methods: {
    debounce(fn, ms) {
      return (...args) => {
        if (this._debTimer) clearTimeout(this._debTimer)
        this._debTimer = setTimeout(() => {
          this._debTimer = null
          fn.apply(this, args)
        }, ms)
      }
    },
    measure() {
      if (!this.visible) return
      const vh =
        window.innerHeight ||
        document.documentElement.clientHeight ||
        0
      let bottomPx = MIN_ABOVE_BOTTOM_NAV_PX
      const btn = document.querySelector('[data-mobile-refresh-fab]')
      if (btn) {
        const cs = window.getComputedStyle(btn)
        const r = btn.getBoundingClientRect()
        const hidden =
          cs.display === 'none' ||
          cs.visibility === 'hidden' ||
          r.width < 4 ||
          r.height < 4
        if (!hidden) {
          const aboveRefresh = vh - r.top + GAP_ABOVE_REFRESH_PX
          bottomPx = Math.max(MIN_ABOVE_BOTTOM_NAV_PX, aboveRefresh)
        }
      }
      this.dynamicBottomPx = bottomPx
    },
  },
}
</script>

<style>
.mobile-ai-floating-chip {
  position: fixed;
  right: 10px;
  z-index: 200;
  width: 50px;
  height: 50px;
  padding: 0;
  border: none;
  border-radius: 25px;
  background: linear-gradient(180deg, #2fb3ff 0%, #0c98fe 55%, #0a7fd2 100%);
  color: #fff;
  font-weight: 700;
  font-size: 0.85rem;
  letter-spacing: 0.06em;
  border: 1px solid rgba(255, 255, 255, 0.35);
  box-shadow:
    0 18px 38px rgba(12, 152, 254, 0.24),
    0 6px 16px rgba(2, 132, 199, 0.25),
    inset 0 1px 0 rgba(255, 255, 255, 0.35);
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.mobile-ai-floating-chip:active {
  opacity: 0.98;
  transform: translateY(1px);
  box-shadow:
    0 10px 26px rgba(12, 152, 254, 0.18),
    0 3px 10px rgba(2, 132, 199, 0.18),
    inset 0 1px 0 rgba(255, 255, 255, 0.28);
}
</style>
